import React, { useState, useEffect } from 'react';
import { Tabs, Card, Button, Table, Space, Tag, Badge, Modal, Spin, message, Empty } from 'antd';
import { PlusOutlined, DeleteOutlined, SendOutlined } from '@ant-design/icons';
import { CampaignBuilder } from './CampaignBuilder';
import { PrintQueue } from './PrintQueue';
import api from '../../services/api';
import './MarketingDashboard.css';

interface Campaign {
  id: number;
  name: string;
  campaign_type: string;
  status: string;
  target_count: number;
  sent_count: number;
  created_at: string;
  updated_at: string;
}

interface Template {
  id: number;
  name: string;
  template_type: string;
  active: number;
}

export const MarketingDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('campaigns');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCampaignBuilder, setShowCampaignBuilder] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Columns for campaigns table
  const campaignColumns = [
    {
      title: 'Campaign Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Type',
      dataIndex: 'campaign_type',
      key: 'campaign_type',
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>{type.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Badge
          status={getStatusBadgeStatus(status)}
          text={status.charAt(0).toUpperCase() + status.slice(1)}
        />
      ),
    },
    {
      title: 'Target Count',
      dataIndex: 'target_count',
      key: 'target_count',
      render: (count: number) => <strong>{count}</strong>,
    },
    {
      title: 'Sent',
      dataIndex: 'sent_count',
      key: 'sent_count',
      render: (count: number, record: Campaign) => (
        <span>
          {count} / {record.target_count}
        </span>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_: unknown, record: Campaign) => (
        <Space size="small">
          {record.status === 'draft' && (
            <Button
              type="primary"
              size="small"
              icon={<SendOutlined />}
              onClick={() => handleExecuteCampaign(record.id)}
              loading={loading}
            >
              Send
            </Button>
          )}
          {record.status !== 'cancelled' && (
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleCancelCampaign(record.id)}
            >
              Cancel
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const templateColumns = [
    {
      title: 'Template Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Type',
      dataIndex: 'template_type',
      key: 'template_type',
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>{type.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active: number) => (
        <Badge status={active ? 'success' : 'error'} text={active ? 'Active' : 'Inactive'} />
      ),
    },
  ];

  // Load campaigns
  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/marketing/campaigns');
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      message.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  // Load templates
  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/marketing/templates');
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
      message.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  // Execute campaign
  const handleExecuteCampaign = async (campaignId: number) => {
    Modal.confirm({
      title: 'Execute Campaign',
      content: 'Are you sure you want to execute this campaign? This will create marketing sends for all target customers.',
      okText: 'Execute',
      cancelText: 'Cancel',
      onOk: async () => {
        setLoading(true);
        try {
          await api.post(`/admin/marketing/campaigns/${campaignId}/send`);
          message.success('Campaign executed successfully');
          setRefreshTrigger((prev) => prev + 1);
          loadCampaigns();
        } catch (error) {
          console.error('Error executing campaign:', error);
          message.error('Failed to execute campaign');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Cancel campaign
  const handleCancelCampaign = async (campaignId: number) => {
    Modal.confirm({
      title: 'Cancel Campaign',
      content: 'Are you sure you want to cancel this campaign?',
      okText: 'Cancel Campaign',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        setLoading(true);
        try {
          await api.delete(`/admin/marketing/campaigns/${campaignId}`);
          message.success('Campaign cancelled successfully');
          loadCampaigns();
        } catch (error) {
          console.error('Error cancelling campaign:', error);
          message.error('Failed to cancel campaign');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Handle campaign creation
  const handleCampaignCreated = () => {
    setShowCampaignBuilder(false);
    loadCampaigns();
  };

  // Load initial data
  useEffect(() => {
    loadCampaigns();
    loadTemplates();
  }, [refreshTrigger]);

  // Determine color for campaign type
  function getTypeColor(type: string): string {
    const colors: Record<string, string> = {
      birthday: 'blue',
      christmas: 'red',
      thank_you: 'green',
      maintenance_reminder: 'orange',
      custom_blast: 'purple',
      reengagement: 'magenta',
    };
    return colors[type] || 'default';
  }

  // Determine badge status
  function getStatusBadgeStatus(status: string): 'success' | 'error' | 'warning' | 'default' | 'processing' {
    switch (status) {
      case 'active':
      case 'completed':
        return 'success';
      case 'draft':
        return 'default';
      case 'scheduled':
        return 'processing';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  }

  return (
    <div className="marketing-dashboard">
      <Spin spinning={loading}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'campaigns',
              label: 'Campaigns',
              children: (
                <Card>
                  <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
                    <h2>Marketing Campaigns</h2>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setShowCampaignBuilder(true)}
                    >
                      Create Campaign
                    </Button>
                  </div>
                  {campaigns.length > 0 ? (
                    <Table
                      columns={campaignColumns}
                      dataSource={campaigns}
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                      loading={loading}
                    />
                  ) : (
                    <Empty description="No campaigns found" />
                  )}
                </Card>
              ),
            },
            {
              key: 'print-queue',
              label: 'Print Queue',
              children: <PrintQueue />,
            },
            {
              key: 'templates',
              label: 'Templates',
              children: (
                <Card>
                  <h2>Mail Templates</h2>
                  {templates.length > 0 ? (
                    <Table
                      columns={templateColumns}
                      dataSource={templates}
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                      loading={loading}
                    />
                  ) : (
                    <Empty description="No templates found" />
                  )}
                </Card>
              ),
            },
          ]}
        />

        {/* Campaign Builder Modal */}
        <Modal
          title="Create New Campaign"
          open={showCampaignBuilder}
          onCancel={() => setShowCampaignBuilder(false)}
          width={900}
          footer={null}
        >
          <CampaignBuilder onCampaignCreated={handleCampaignCreated} />
        </Modal>
      </Spin>
    </div>
  );
};

export default MarketingDashboard;
